import { useRef, useEffect, useCallback, useMemo } from 'react'
import { useTickerEvents, useRealtimeTicker } from '../hooks/useTicker'
import './Ticker.css'

const EVENT_ICONS = {
  ace: '\u{1F3C6}',
  match_tied: '\u{1F91D}',
  lead_flip: '\u{1F504}',
  segment_clinched_front: '\u{1F3C6}',
  segment_clinched_back: '\u{1F3C6}',
  segment_clinched_overall: '\u{1F3C6}',
  dormie_front: '\u{26A0}\u{FE0F}',
  dormie_back: '\u{26A0}\u{FE0F}',
  dormie_overall: '\u{26A0}\u{FE0F}',
  score_summary: '\u{26F3}',
}

const EXPIRY_MS = 30 * 60 * 1000 // 30 minutes
const SCROLL_PX_PER_SEC = 80
const ALLOWED_TYPES = new Set(Object.keys(EVENT_ICONS))

function filterAndDedup(events) {
  if (!events || events.length === 0) return []

  const now = Date.now()
  const cutoff = now - EXPIRY_MS

  // Separate summaries from regular events, filtering out retired event types
  const summaries = []
  const regular = []

  for (const e of events) {
    if (!ALLOWED_TYPES.has(e.event_type)) continue
    const ts = new Date(e.created_at).getTime()
    if (e.event_type === 'score_summary') {
      summaries.push(e)
    } else if (ts >= cutoff) {
      regular.push(e)
    }
  }

  // Dedup summaries: keep only latest per game_id
  const latestByGame = new Map()
  for (const s of summaries) {
    const existing = latestByGame.get(s.game_id)
    if (!existing || new Date(s.created_at) > new Date(existing.created_at)) {
      latestByGame.set(s.game_id, s)
    }
  }

  // Summaries first, then recent events (already sorted newest-first from API)
  return [...latestByGame.values(), ...regular]
}

export default function Ticker({ matchId }) {
  const { data: events } = useTickerEvents(matchId)
  useRealtimeTicker(matchId)
  const trackRef = useRef(null)
  const scrollRef = useRef({ offset: 0, halfWidth: 0 })
  const pausedRef = useRef(false)
  const rafRef = useRef(null)
  const lastTimeRef = useRef(null)

  const items = useMemo(() => filterAndDedup(events), [events])

  // Recalculate halfWidth when items change; preserve proportional scroll position
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    const newHalf = el.scrollWidth / 2
    if (newHalf <= 0) return
    const prev = scrollRef.current
    if (prev.halfWidth > 0 && prev.halfWidth !== newHalf) {
      prev.offset = (prev.offset / prev.halfWidth) * newHalf
    }
    prev.halfWidth = newHalf
  }, [items])

  // Single rAF loop — runs for the lifetime of the component
  useEffect(() => {
    const step = (timestamp) => {
      const el = trackRef.current
      if (el && !pausedRef.current) {
        if (lastTimeRef.current === null) lastTimeRef.current = timestamp
        const delta = timestamp - lastTimeRef.current
        lastTimeRef.current = timestamp

        const s = scrollRef.current
        s.offset -= (SCROLL_PX_PER_SEC * delta) / 1000
        if (s.halfWidth > 0 && s.offset <= -s.halfWidth) {
          s.offset += s.halfWidth
        }
        el.style.transform = `translateX(${s.offset}px)`
      } else {
        lastTimeRef.current = null
      }
      rafRef.current = requestAnimationFrame(step)
    }
    rafRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  const pause = useCallback(() => { pausedRef.current = true }, [])
  const resume = useCallback(() => { pausedRef.current = false }, [])

  return (
    <div
      className="ticker-wrap"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onTouchStart={pause}
      onTouchEnd={resume}
    >
      <div className="ticker-badge">
        <span className="ticker-badge-dot" />
        LIVE
      </div>
      <div className="ticker-scroll-area">
        {items.length === 0 ? (
          <div className="ticker-track">
            <span className="ticker-item ticker-item-summary">Match in progress</span>
          </div>
        ) : (
          <div ref={trackRef} className="ticker-track">
            {/* Render items twice for seamless loop */}
            {[0, 1].map((copy) =>
              items.map((event, i) => (
                <span key={`${copy}-${event.id || i}`} className={`ticker-item${event.event_type === 'score_summary' ? ' ticker-item-summary' : ''}`}>
                  <span className="ticker-item-icon">
                    {EVENT_ICONS[event.event_type] || '\u{26F3}'}
                  </span>
                  {event.headline}
                  {(i < items.length - 1 || copy === 0) && (
                    <span className="ticker-separator">&bull;</span>
                  )}
                </span>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}
